/**
 * VA-27640: reading the coding time cap the backend sends.
 *
 * The backend sizes each coding question's cap from the interview's configured
 * value and the remaining clock, and sends it as `time_limit` / `time_limit_minutes`
 * (minutes). It may send NEITHER — that means "this question has no cap", and the
 * frontend must not invent one (the old hardcoded 15-minute default is exactly what
 * made an interview configured for 4-minute questions count down from 14:55).
 *
 * "No cap" is therefore a real state, distinct from "a cap of zero". Collapsing the
 * two is not safe: a total of 0 seconds makes the editor's timer read 00:00 the
 * instant it opens, which trips the auto-submit-on-expiry effect and submits an
 * empty answer before the candidate can type. These helpers keep the distinction
 * explicit so callers have to decide which one they mean.
 */
/**
 * The question's cap in whole seconds, or `null` when it has no cap.
 *
 * Non-finite and non-positive values are treated as "no cap" rather than as a
 * usable total — they cannot drive a countdown, and pretending otherwise is what
 * produced the instant-expiry behaviour described above.
 */
export function codingTotalSec(question) {
    const minutes = question === null || question === void 0 ? void 0 : question.time_estimate;
    if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0)
        return null;
    return Math.round(minutes * 60);
}
/**
 * The question's cap in seconds for arithmetic and BOOKKEEPING ONLY — time-spent
 * accounting, log lines, and the `time_estimate` field echoed back to the backend —
 * where an uncapped question contributes nothing and 0 is the correct identity.
 *
 * Do NOT use this to drive a countdown or an expiry check; use `codingTotalSec` and
 * branch on `null` so an uncapped question simply has no timer.
 */
export function codingTotalSecOrZero(question) {
    var _a;
    return (_a = codingTotalSec(question)) !== null && _a !== void 0 ? _a : 0;
}
/** True when the backend gave this question a usable time cap. */
export function hasCodingTimeCap(question) {
    return codingTotalSec(question) !== null;
}
